/**
 * The effort axis — how much work a recommendation is, shared by the AI Audit Recommendations tab
 * and the Persona Recommendations tab.
 *
 * The API sends a bare band (`quick` | `moderate` | `substantial`); the time ranges live here
 * rather than on the backend so wording can change without a deploy on both sides. Both screens
 * read these maps, so a band the API adds later falls back to its raw value everywhere instead of
 * being relabelled as one of the three.
 */

/** The effort bands the API can send, in ascending order of work. */
export const EFFORT_ORDER = ["quick", "moderate", "substantial"] as const;

export type EffortLevel = (typeof EFFORT_ORDER)[number];

/**
 * Chip text on a recommendation card. Kept short — the chip sits in a row with the impact badge and
 * one or two others, and has to survive a narrow card.
 */
export const EFFORT_LABELS: Record<EffortLevel, string> = {
  quick: "Quick · under 1 hr",
  moderate: "Moderate · 1-4 hrs",
  substantial: "Substantial · 1 day+",
};

/** Dropdown text, which has room to spell the range out. */
export const EFFORT_FILTER_LABELS: Record<EffortLevel, string> = {
  quick: "Quick — under 1 hour",
  moderate: "Moderate — a few hours",
  substantial: "Substantial — a day or more",
};

/**
 * Chip colours, which deliberately leave the severity ramp alone.
 *
 * Effort is not a severity, and a card can legitimately read "Critical" next to "Quick" — a blocked
 * crawler is urgent and takes one line of robots.txt. Reusing the red/orange ramp the impact badge
 * owns would make that pairing look like a bug, so only the actionable end is coloured: green marks
 * the work you can finish today, and the largest band stays neutral rather than shouting.
 */
export const EFFORT_CHIP_COLORS: Record<EffortLevel, string> = {
  quick: "green",
  moderate: "yellow",
  substantial: "neutral",
};

/** Options for the Effort filter dropdown, before per-option counts are attached. */
export const EFFORT_FILTER_OPTIONS = EFFORT_ORDER.map((value) => ({
  value,
  label: EFFORT_FILTER_LABELS[value],
}));
